import React from "react";
import PropTypes from "prop-types";
import { useFormContext } from "react-hook-form";
import { LabelWrapper } from "./LabelWrapper";

const getFormFieldWrapperProps = (props) => {
	// TLDR: This registers the input with the RHF form.

	// this function handles the logic of extracting the props needed
	// for the wrapper from the props needed for the input itself
	// as well as the props needed for the register function

	// remove props that are not needed for the input
	// everything not pulled out here will be passed to the input
	const {
		name,
		type,
		icon,
		label,
		helpText,
		required, // separate from the RHF register() option
		errorMessage, // more specific, custom error message
		// RHF register() options
		registerOptions,
		// everything else will flow through to the input
		...inputProps
	} = props;

	// funcs and vars from RHF's useForm hook
	const { register, getFieldState, formState } = useFormContext();

	// Register the input with RHF
	const { ref, ...inputRegisterProps } = register(name, registerOptions);
	// pull out the ref RHF generates since we don't want to pass it down,
	// we need to pass it down as `getRef` instead

	// props about the field state
	const fieldState = getFieldState(props.name, formState);
	const { isDirty, isTouched, invalid, error } = fieldState;

	const inputId = `${name}-${type}-input`;
	// props for the wrapper
	const wrapperProps = {
		isDirty, // field has been modified
		isTouched, // field has been focused and blurred
		invalid, // field is not valid
		error, // field error object
		errorMessage, // custom error message
		htmlFor: inputId, // to be passed to the for attr on the label
		type,
		required,
		icon,
		label,
		helpText
	};

	const newInputProps = {
		id: inputId,
		...inputProps,
		// this is what the register function returns to be put on the input
		...inputRegisterProps,
		getRef: ref //
	};

	return {
		wrapperProps,
		inputProps: newInputProps
	};
};

// FormFieldWrapper = LabelWrapper + Input
export const FormFieldWrapper = ({ children, ...props }) => {
	// This component implements getFormFieldWrapperProps so that we end up with
	// two chunks of props, one to forward to the input, one to apply to the wrapper.

	const { inputProps, wrapperProps } = getFormFieldWrapperProps(props);
	return (
		<LabelWrapper {...wrapperProps}>
			{React.cloneElement(React.Children.only(children), {
				...inputProps
			})}
		</LabelWrapper>
	);
};

FormFieldWrapper.propTypes = {
	children: PropTypes.element.isRequired
};
